import { useState, useEffect } from 'react';
import { Toaster, toast } from 'react-hot-toast';
import {
  getFinanceSummary,
  getReceiptWiseReport,
  getModelWiseReport,
  downloadFullBackupZip,
  restoreFromBackupZip,
} from '../utils/api';
import { downloadCsv, rowsFromObjects } from '../utils/csvExport';

const REPORT_DATE = () => new Date().toISOString().split('T')[0];

const SETTLEMENT_HEADERS = [
  'Order Number',
  'Customer Name',
  'Phone',
  'Order Date',
  'Status',
  'Grand Total (₹)',
  'Advance Received (₹)',
  'Balance Remaining (₹)',
  'Settlement Status',
];

const RECEIPT_HEADERS = [
  'Receipt No',
  'Order No',
  'Date',
  'Customer Name',
  'Phone',
  'Type',
  'Status',
  'Item Lines',
  'Grand Total (₹)',
  'Advance (₹)',
  'Balance (₹)',
  'Settlement Status',
  'Payment ID',
  'Receipt Locale',
];

const RECEIPT_KEYS = [
  'receiptNo',
  'orderNo',
  'date',
  'customerName',
  'phone',
  'type',
  'status',
  'itemLines',
  'grandTotal',
  'advance',
  'balance',
  'settlementStatus',
  'paymentId',
  'receiptLocale',
];

const MODEL_HEADERS = [
  'Order No',
  'Receipt No',
  'Order Date',
  'Customer Name',
  'Phone',
  'Order Type',
  'Order Status',
  'Model No',
  'Model Name',
  'Qty',
  'Unit Price (₹)',
  'Line Total (₹)',
  'Line Advance (₹)',
  'Color Request',
];

const MODEL_KEYS = [
  'orderNo',
  'receiptNo',
  'orderDate',
  'customerName',
  'phone',
  'orderType',
  'orderStatus',
  'modelNo',
  'modelName',
  'qty',
  'unitPrice',
  'lineTotal',
  'lineAdvance',
  'colorRequest',
];

const FinancePage = () => {
  const [loading, setLoading] = useState(true);
  const [financeData, setFinanceData] = useState(null);
  const [exportingReport, setExportingReport] = useState(null);
  const [backupFile, setBackupFile] = useState(null);
  const [restorePassword, setRestorePassword] = useState('');
  const [restoreConfirmed, setRestoreConfirmed] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);

  const fetchFinanceData = async () => {
    try {
      setLoading(true);
      const data = await getFinanceSummary();
      setFinanceData(data);
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Failed to load finance data';
      toast.error(message);
      console.error('Error fetching finance data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFinanceData();
  }, []);

  const formatCurrency = (amount) => {
    return `₹${(amount || 0).toLocaleString('en-IN')}`;
  };

  const renderOrdersTable = (orders, emptyTitle, emptyMessage, { showRetained = false } = {}) => {
    if (!orders || orders.length === 0) {
      return (
        <div className="p-8 text-center text-gray-600">
          <div className="text-4xl mb-4">📊</div>
          <p className="text-lg font-medium mb-2">{emptyTitle}</p>
          <p className="text-sm">{emptyMessage}</p>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Order Details
              </th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Customer
              </th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Financial Summary
              </th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Settlement Status
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {orders.map((order) => (
              <tr key={`${order.orderNo}-${order.status}`} className="hover:bg-gray-50">
                <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900">{order.orderNo}</div>
                    <div className="text-sm text-gray-500">
                      {new Date(order.date).toLocaleDateString('en-IN')}
                    </div>
                  </div>
                </td>
                <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900">{order.customerName}</div>
                    <div className="text-sm text-gray-500">{order.phone}</div>
                  </div>
                </td>
                <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full capitalize ${
                      order.status === 'dispatched'
                        ? 'bg-blue-100 text-blue-800'
                        : order.status === 'completed'
                          ? 'bg-green-100 text-green-800'
                          : order.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : order.status === 'cancelled'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {order.status}
                  </span>
                </td>
                <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900 mb-1">
                      Total: {formatCurrency(order.grandTotal)}
                    </div>
                    <div className="text-sm text-gray-600 mb-1">
                      Advance: {formatCurrency(order.totalAdvance)}
                    </div>
                    {!showRetained && (
                      <div className={`text-sm ${order.balance === 0 ? 'text-green-600 font-medium' : 'text-orange-600'}`}>
                        {order.balance === 0 ? 'Fully Paid' : `Balance: ${formatCurrency(order.balance)}`}
                      </div>
                    )}
                    {showRetained && order.totalAdvance > 0 && (
                      <div className="text-sm text-amber-700 font-medium mt-1">
                        Retained: {formatCurrency(order.totalAdvance)}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-3 sm:px-6 py-4 whitespace-normal sm:whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full whitespace-normal sm:whitespace-nowrap ${
                    order.settlementStatus === 'Advance Retained'
                      ? 'bg-amber-100 text-amber-800'
                      : order.balance === 0
                        ? 'bg-green-100 text-green-800'
                        : 'bg-orange-100 text-orange-800'
                  }`}>
                    {order.settlementStatus}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const exportToCSV = () => {
    try {
      if (!exportSettlementCsv()) {
        toast.error('No data available for export');
        return;
      }
      toast.success('Settlement report exported successfully!');
    } catch (error) {
      console.error('Error exporting CSV:', error);
      const message = error.response?.data?.message || error.message || 'Failed to export CSV';
      toast.error(message);
    }
  };

  const exportReceiptWiseReport = async () => {
    try {
      setExportingReport('receipt');
      const { rows } = await getReceiptWiseReport();
      if (!rows?.length) {
        toast.error('No data available for export');
        return;
      }
      downloadCsv(
        `receipt_wise_report_${REPORT_DATE()}.csv`,
        RECEIPT_HEADERS,
        rowsFromObjects(rows, RECEIPT_KEYS)
      );
      toast.success('Receipt-wise report exported successfully!');
    } catch (error) {
      console.error('Error exporting receipt-wise report:', error);
      toast.error(error.message || 'Failed to export receipt-wise report');
    } finally {
      setExportingReport(null);
    }
  };

  const exportModelWiseReport = async () => {
    try {
      setExportingReport('model');
      const { rows } = await getModelWiseReport();
      if (!rows?.length) {
        toast.error('No data available for export');
        return;
      }
      downloadCsv(
        `model_wise_report_${REPORT_DATE()}.csv`,
        MODEL_HEADERS,
        rowsFromObjects(rows, MODEL_KEYS)
      );
      toast.success('Model-wise report exported successfully!');
    } catch (error) {
      console.error('Error exporting model-wise report:', error);
      toast.error(error.message || 'Failed to export model-wise report');
    } finally {
      setExportingReport(null);
    }
  };

  const exportSettlementCsv = () => {
    const pendingOrders = financeData?.pendingOrders || [];
    const settledOrders = financeData?.settledOrders || financeData?.orders || [];
    const cancelledOrders = financeData?.cancelledOrders || [];
    const allOrders = [...pendingOrders, ...settledOrders, ...cancelledOrders];

    if (allOrders.length === 0) {
      return false;
    }

    const rows = allOrders.map((order) => [
      order.orderNo,
      order.customerName,
      order.phone,
      new Date(order.date).toLocaleDateString('en-IN'),
      order.status,
      order.grandTotal,
      order.totalAdvance,
      order.balance,
      order.settlementStatus,
    ]);

    downloadCsv(`finance_settlement_${REPORT_DATE()}.csv`, SETTLEMENT_HEADERS, rows);
    return true;
  };

  const exportFullBackup = async () => {
    if (exportingReport) return;

    try {
      setExportingReport('full');
      toast.loading('Building backup ZIP (data + photos)…', { id: 'full-backup' });
      await downloadFullBackupZip();
      toast.success('Full backup ZIP downloaded', { id: 'full-backup' });
    } catch (error) {
      console.error('Error exporting full backup:', error);
      toast.error(error.message || 'Full backup failed', { id: 'full-backup' });
    } finally {
      setExportingReport(null);
    }
  };

  const handleRestoreBackup = async () => {
    if (!backupFile || !restorePassword.trim() || !restoreConfirmed || restoring) return;

    try {
      setRestoring(true);
      setRestoreResult(null);
      toast.loading('Restoring backup…', { id: 'restore-backup' });

      const result = await restoreFromBackupZip(backupFile, restorePassword.trim());
      setRestoreResult(result);
      toast.success(result.message || 'Backup restored', { id: 'restore-backup' });
      setBackupFile(null);
      setRestorePassword('');
      setRestoreConfirmed(false);
      await fetchFinanceData();
    } catch (error) {
      console.error('Restore backup error:', error);
      toast.error(error.message || 'Restore failed', { id: 'restore-backup' });
    } finally {
      setRestoring(false);
    }
  };

  const summary = financeData?.summary || {};
  const pendingOrders = financeData?.pendingOrders || [];
  const settledOrders = [...(financeData?.settledOrders || financeData?.orders || [])].sort((a, b) => {
    const rank = (status) => (status === 'dispatched' ? 0 : status === 'completed' ? 1 : 2);
    return rank(a.status) - rank(b.status);
  });
  const cancelledOrders = financeData?.cancelledOrders || [];
  const hasExportData = pendingOrders.length > 0 || settledOrders.length > 0 || cancelledOrders.length > 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-300 border-t-blue-600"></div>
          <span className="text-blue-700 font-medium">Loading finance data...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <Toaster position="top-right" />
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-100 py-8">
        <div className="container mx-auto px-4 max-w-7xl">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-4xl font-bold text-gray-800 mb-2">
            Financial Accounting
          </h1>
          <p className="text-gray-600">Settlement reports and financial overview across all active orders</p>
        </div>

        <h2 className="text-xl font-semibold text-gray-800 mb-4">Overall Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-orange-500">
            <h3 className="text-lg font-semibold text-gray-800">Total Outstanding (All Orders)</h3>
            <p className="text-3xl font-bold text-orange-600 mt-2">
              {formatCurrency(summary.totalOutstanding ?? summary.totalRemainingToCollect ?? 0)}
            </p>
            <p className="text-sm text-gray-500 mt-1">Pending + dispatched + completed (non-cancelled)</p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-gray-800">Revenue Expected</h3>
            <p className="text-3xl font-bold text-green-600 mt-2">
              {formatCurrency(summary.totalRevenueExpected)}
            </p>
            <p className="text-sm text-gray-500 mt-1">All active orders</p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-gray-800">Advance Received</h3>
            <p className="text-3xl font-bold text-purple-600 mt-2">
              {formatCurrency(summary.totalAdvanceReceived)}
            </p>
            <p className="text-sm text-gray-500 mt-1">All active orders</p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-amber-500">
            <h3 className="text-lg font-semibold text-gray-800">Advance Retained (Cancelled)</h3>
            <p className="text-3xl font-bold text-amber-700 mt-2">
              {formatCurrency(summary.advanceRetainedOnCancelled)}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              {summary.cancelledOrdersCount || 0} cancelled order(s), advance not refunded
            </p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-gray-800">Settlement Rate</h3>
            <p className="text-3xl font-bold text-indigo-600 mt-2">
              {summary.settlementRate ?? 0}%
            </p>
            <p className="text-sm text-gray-500 mt-1">Based on advance collected vs total order value</p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-gray-800">Remaining After Dispatch</h3>
            <p className="text-3xl font-bold text-amber-600 mt-2">
              {formatCurrency(summary.totalRemainingAfterDispatch ?? summary.dispatchedBalanceDue ?? 0)}
            </p>
            <p className="text-sm text-gray-500 mt-1">Balance due on dispatched/completed orders</p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-gray-800">Fully Settled Orders</h3>
            <p className="text-3xl font-bold text-emerald-600 mt-2">
              {summary.totalFullySettled || 0}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              of {summary.totalOrdersDispatched || 0} dispatched/completed
            </p>
          </div>
        </div>

        <h2 className="text-xl font-semibold text-gray-800 mb-4">Pending Orders (Not Yet Dispatched)</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-gray-800">Pending Orders</h3>
            <p className="text-3xl font-bold text-yellow-600 mt-2">
              {summary.pendingOrdersCount || 0}
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-gray-800">Advance on Pending</h3>
            <p className="text-3xl font-bold text-purple-600 mt-2">
              {formatCurrency(summary.pendingAdvanceReceived)}
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-red-500">
            <h3 className="text-lg font-semibold text-gray-800">Balance Due Before Dispatch</h3>
            <p className="text-3xl font-bold text-red-600 mt-2">
              {formatCurrency(summary.pendingBalanceDue)}
            </p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800">Pending Orders Summary</h3>
            <p className="text-gray-600 text-sm mt-1">
              Orders awaiting dispatch with outstanding balance
            </p>
          </div>
          {renderOrdersTable(
            pendingOrders,
            'No Pending Orders',
            'All orders have been dispatched or there are no active orders.'
          )}
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Export Reports</h3>
            <p className="text-gray-600 text-sm">
              Download a ZIP backup with inventory photos, orders, counters, and CSV reports. Restore merges backup data into the database (updates existing records, adds missing ones).
            </p>
          </div>
          <div className="flex flex-col sm:flex-row flex-wrap gap-3">
            <button
              type="button"
              onClick={exportFullBackup}
              disabled={exportingReport !== null || restoring}
              className="px-5 py-3 bg-slate-800 text-white rounded-lg hover:bg-slate-900 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-semibold text-sm"
            >
              {exportingReport === 'full' ? 'Preparing backup…' : 'Download full backup (ZIP)'}
            </button>
            <button
              type="button"
              onClick={exportToCSV}
              disabled={!hasExportData || exportingReport !== null || restoring}
              className="px-5 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-semibold text-sm"
            >
              Export Settlement CSV
            </button>
            <button
              type="button"
              onClick={exportReceiptWiseReport}
              disabled={exportingReport !== null || restoring}
              className="px-5 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-semibold text-sm"
            >
              {exportingReport === 'receipt' ? 'Generating…' : 'Receipt-wise Report'}
            </button>
            <button
              type="button"
              onClick={exportModelWiseReport}
              disabled={exportingReport !== null || restoring}
              className="px-5 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-semibold text-sm"
            >
              {exportingReport === 'model' ? 'Generating…' : 'Model-wise Report'}
            </button>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <h4 className="text-sm font-semibold text-gray-800 mb-2">Restore from backup</h4>
            <p className="text-gray-600 text-sm mb-3">
              Upload a backup ZIP to merge inventory (with photos), orders, and counters. Existing records are updated; missing ones are added. Nothing is deleted.
            </p>
            <div className="flex flex-col gap-3 max-w-lg">
              <input
                type="file"
                accept=".zip,application/zip"
                onChange={(e) => {
                  setBackupFile(e.target.files?.[0] || null);
                  setRestoreResult(null);
                }}
                className="text-sm text-gray-700 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-slate-100 file:text-slate-800"
              />
              <input
                type="password"
                value={restorePassword}
                onChange={(e) => setRestorePassword(e.target.value)}
                placeholder="Restore password"
                className="toolbar-input max-w-full"
                autoComplete="off"
              />
              <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={restoreConfirmed}
                  onChange={(e) => setRestoreConfirmed(e.target.checked)}
                  className="mt-0.5"
                />
                <span>
                  I understand this will update existing records and add missing ones from the backup.
                </span>
              </label>
              <button
                type="button"
                onClick={handleRestoreBackup}
                disabled={
                  !backupFile ||
                  !restorePassword.trim() ||
                  !restoreConfirmed ||
                  restoring ||
                  exportingReport !== null
                }
                className="px-5 py-3 bg-amber-700 text-white rounded-lg hover:bg-amber-800 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-semibold text-sm w-fit"
              >
                {restoring ? 'Restoring…' : 'Restore backup'}
              </button>
            </div>
            {restoreResult?.stats && (
              <div className="mt-4 p-3 bg-gray-50 rounded-md text-sm text-gray-700 space-y-1">
                <p className="font-medium text-gray-800">Last restore summary</p>
                <p>Inventory: {restoreResult.stats.inventoryCreated} created, {restoreResult.stats.inventoryUpdated} updated</p>
                <p>Orders: {restoreResult.stats.ordersCreated} created, {restoreResult.stats.ordersUpdated} updated</p>
                <p>Photos uploaded: {restoreResult.stats.photosUploaded}</p>
                <p>Counters merged: {restoreResult.stats.countersMerged}</p>
                {restoreResult.warnings?.length > 0 && (
                  <div className="mt-2 text-amber-800">
                    <p className="font-medium">Warnings ({restoreResult.warnings.length})</p>
                    <ul className="list-disc list-inside max-h-32 overflow-y-auto">
                      {restoreResult.warnings.slice(0, 10).map((w, i) => (
                        <li key={i}>{w}</li>
                      ))}
                      {restoreResult.warnings.length > 10 && (
                        <li>…and {restoreResult.warnings.length - 10} more</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800">Dispatched & Completed Orders</h3>
            <p className="text-gray-600 text-sm mt-1">
              Orders that have been dispatched or completed and their settlement status
            </p>
            <p className="text-sm text-gray-500 mt-1">
              {summary.dispatchedOrdersCount ?? 0} dispatched · {summary.completedOrdersCount ?? 0} completed
            </p>
          </div>
          {renderOrdersTable(
            settledOrders,
            'No Dispatched Orders Yet',
            'Orders will appear here once they are dispatched from the Orders Management page.'
          )}
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800">Cancelled Orders (Advance Retained)</h3>
            <p className="text-gray-600 text-sm mt-1">
              Cancelled orders where advance was kept per non-refundable policy
            </p>
          </div>
          {renderOrdersTable(
            cancelledOrders,
            'No Cancelled Orders',
            'Cancelled orders will appear here with advance retained.',
            { showRetained: true }
          )}
        </div>
        </div>
      </div>
    </>
  );
};

export default FinancePage;
